import { Edit } from '@mui/icons-material';
import { Box, Button, Grid, LinearProgress, Toolbar, Typography } from '@mui/material';
import {
  ExportCsv,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
} from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { FC, useMemo, useState } from 'react';

import { formatDate } from '../../../../components/formatUtils/dateFormatUtils';
import { useNavigateDialogForm } from '../../../../components/formikDialogForm/dialogForm.hook';
import { AppDataGrid } from '../../../../components/table/AppDataGrid';
import { PaginationState, useFindAllOwnedAufwandQuery } from '../../../aufwand/api/aufwandApi';
import { Aufwand } from '../../../aufwand/api/types';
import { useFindProjektByIdQuery } from '../../api/projektApi';

import { ProjektStammdatenCard } from './ProjektStammdatenCard';

const aufwandTableColumns: GridColDef[] = [
  {
    field: 'taetigkeit',
    headerName: 'Taetigkeit',
    flex: 3,
  },
  {
    field: 'ticketId',
    headerName: 'Ticket Id',
    flex: 2,
  },
  {
    field: 'dauerInMillis',
    headerName: 'Dauer in Std.',
    flex: 1,
    type: 'number',
    valueFormatter: value => ((value as number) / 3600000).toLocaleString('de'),
  },
  {
    field: 'datum',
    headerName: 'Datum',
    flex: 1,
    type: 'date',
    valueFormatter: value => formatDate(value as string),
  },
];

export const ExternalProjektDetails: FC<{ id: string }> = ({ id }) => {
  const navigateToEditAufwand = useNavigateDialogForm<Aufwand>('aufwand', 'edit');

  const columns = useMemo(
    () => [
      ...aufwandTableColumns,
      {
        field: 'edit',
        type: 'actions',
        getActions: params => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => navigateToEditAufwand(params.row as Aufwand)}
          />,
        ],
      } as GridActionsColDef,
    ],
    [navigateToEditAufwand]
  );

  const [sortState, setSortState] = useState<SortState>({ datum: 'desc' });

  interface SortState {
    [key: string]: 'asc' | 'desc';
  }

  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 0,
    pageSize: 25,
  });

  const { data: projekt, isFetching } = useFindProjektByIdQuery(id);
  const { data: aufwaende, isFetching: isFetchingAufwaende } = useFindAllOwnedAufwandQuery({
    projektId: id,
    sort: sortState,
    pagination: paginationState,
  });

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isFetching && <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%' }} />}
      {projekt && (
        <>
          <Typography
            variant={'h5'}
            sx={{
              m: 1,
              color: 'text.secondary',
            }}
          >
            Projekt ({projekt?.name})
          </Typography>
          <ProjektStammdatenCard projekt={projekt} withKundePopover />
        </>
      )}
      <Typography
        variant={'h5'}
        sx={{
          mx: 1,
          mt: 2,
          mb: 1,
          color: 'text.secondary',
        }}
      >
        Aufwände
      </Typography>
      <AppDataGrid
        style={{ flexGrow: 1 }}
        loading={isFetchingAufwaende}
        onRowClick={() => {}}
        pagination
        paginationMode="server"
        rowCount={aufwaende?.pagination.count ?? 0}
        paginationModel={paginationState}
        onPaginationModelChange={setPaginationState}
        pageSizeOptions={[5, 25, 50, 100, 200]}
        columns={columns}
        rows={aufwaende?.data ?? []}
        sortingMode="server"
        onSortModelChange={model =>
          setSortState(
            model.reduce((acc, cur) => {
              acc[cur.field] = cur.sort ? (_.upperCase(cur.sort) as 'asc' | 'desc') : 'asc';
              return acc;
            }, {} as SortState)
          )
        }
        initialState={{
          sorting: { sortModel: [{ field: 'datum', sort: 'desc' }] },
        }}
        slots={{
          toolbar: () => (
            <Toolbar>
              <Grid
                container
                sx={{
                  justifyContent: 'flex-end',
                  width: '100%',
                }}
              >
                <ExportCsv
                  options={{
                    includeHeaders: true,
                    delimiter: ';',
                    fileName: 'EffortLog-Aufwände',
                    utf8WithBom: true,
                  }}
                  render={
                    <Button variant="contained" color="secondary">
                      Export CSV
                    </Button>
                  }
                />
              </Grid>
            </Toolbar>
          ),
        }}
        checkboxSelection
      />
    </Box>
  );
};
